/**
 * Pure statistical functions for pain data aggregation.
 *
 * All numeric helpers are dependency-free and operate on plain number arrays.
 * The wrappers (`computePainStats`) accept the package's `PainRecord` shape.
 */

import type { PainRecord } from "./types";

// ---------------------------------------------------------------------------
// Low-level math helpers
// ---------------------------------------------------------------------------

/**
 * Arithmetic mean. Returns `null` for an empty array.
 */
export function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sum = values.reduce((acc, v) => acc + v, 0);
  return sum / values.length;
}

/**
 * Median value. Returns `null` for an empty array.
 * For even-length arrays, returns the average of the two middle values.
 */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

/**
 * Population standard deviation (σ, divides by N — not N-1).
 * Returns `null` when fewer than 2 values are provided.
 *
 * This matches the existing server-side export formula (pdf-export-utils).
 */
export function stddev(values: readonly number[]): number | null {
  if (values.length < 2) return null;
  const m = mean(values);
  if (m === null) return null;
  const variance =
    values.reduce((acc, v) => acc + (v - m) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

/**
 * Round a nullable number to the given number of decimal places.
 */
export function roundTo(
  value: number | null,
  decimals: number,
): number | null {
  if (value === null) return null;
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

// ---------------------------------------------------------------------------
// Composite pain statistics
// ---------------------------------------------------------------------------

/** Result shape returned by `computePainStats`. */
export interface PainStats {
  mean: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
  stddev: number | null;
  /** Number of data points used to compute the above. */
  count: number;
}

/**
 * Compute pain statistics from an array of raw pain‐level numbers.
 *
 * All values are rounded to 1 decimal place, matching the behaviour of the
 * existing `calculatePainStats` in `pdf-export-utils.ts`.
 */
export function computePainStatsFromValues(values: readonly number[]): PainStats {
  if (values.length === 0) {
    return { mean: null, median: null, min: null, max: null, stddev: null, count: 0 };
  }
  return {
    mean: roundTo(mean(values), 1),
    median: roundTo(median(values), 1),
    min: roundTo(Math.min(...values), 1),
    max: roundTo(Math.max(...values), 1),
    stddev: roundTo(stddev(values), 1),
    count: values.length,
  };
}

/**
 * Compute pain statistics from `PainRecord[]`.
 *
 * By default this follows the **export convention**: records with no pain
 * locations (empty `painLocations`) are excluded entirely — they do NOT
 * contribute a 0 to the distribution. This matches `buildExportStats` which
 * flattens all `intensityScore` values and ignores empty records.
 *
 * Set `opts.treatEmptyAsZero` to `true` for UI scenarios where a check-in
 * with 0 locations is intentionally "pain-free = 0".
 */
export function computePainStats(
  records: readonly PainRecord[],
  opts?: { treatEmptyAsZero?: boolean },
): PainStats {
  const values: number[] = [];
  for (const r of records) {
    if (r.painLocations.length === 0) {
      if (opts?.treatEmptyAsZero) {
        values.push(0);
      }
      // else: skip — export convention
      continue;
    }
    for (const loc of r.painLocations) {
      values.push(loc.intensityScore);
    }
  }
  return computePainStatsFromValues(values);
}
